import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  ChefHat,
  RefreshCw,
  Search,
  SkipForward,
  UtensilsCrossed,
  XCircle,
} from "lucide-react";
import { api, ApiRequestError, type Me } from "../lib/api";
import type { OrderView, StaffOrdersResponse } from "../lib/types";
import { PageHeader } from "../components/PageHeader";
import { RealtimeBadge } from "../components/RealtimeBadge";
import { formatRupiah } from "../lib/format";
import { useRealtime } from "../lib/realtime";

const statusLabels: Record<string, string> = {
  queued: "Baru",
  preparing: "Dimasak",
  ready: "Siap",
  served: "Disajikan",
  completed: "Selesai",
  cancelled: "Dibatalkan",
};

export function OrdersPage({ me }: { me: Me }) {
  const [orders, setOrders] = useState<OrderView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const response = await api<StaffOrdersResponse>("/api/staff/orders");
      setOrders(response.orders);
      setError(null);
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Tidak dapat memuat pesanan.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const realtime = useRealtime({
    staff: true,
    onEvent: () => {
      void load();
    },
  });

  // Fallback lambat hanya saat koneksi realtime terputus.
  useEffect(() => {
    void load();
    if (realtime.connected) return;
    const timer = window.setInterval(() => void load(), 15_000);
    return () => window.clearInterval(timer);
  }, [load, realtime.connected]);

  async function updateOrder(order: OrderView, status: string) {
    setBusy(order.id);
    try {
      const response = await api<{ order: OrderView }>(
        `/api/staff/orders/${order.id}/status`,
        { method: "PATCH", body: JSON.stringify({ status }) },
      );
      setOrders((current) =>
        current.map((row) => (row.id === order.id ? response.order : row)),
      );
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Gagal memperbarui pesanan.",
      );
    } finally {
      setBusy(null);
    }
  }

  const filtered = useMemo(() => {
    return orders.filter((order) => {
      if (filter !== "all" && order.status !== filter) return false;
      if (!search.trim()) return true;
      const needle = search.trim().toLowerCase();
      return (
        (order.orderNo ?? "").toLowerCase().includes(needle) ||
        (order.customerPhone ?? "").toLowerCase().includes(needle) ||
        (order.table?.label ?? "").toLowerCase().includes(needle)
      );
    });
  }, [filter, orders, search]);

  const canKitchen = ["owner", "admin", "supervisor", "kitchen"].includes(
    me.membership.role,
  );
  const canServe = ["owner", "admin", "supervisor", "waiter"].includes(
    me.membership.role,
  );
  const canCancel = ["owner", "admin", "supervisor", "cashier"].includes(
    me.membership.role,
  );

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Operasional"
        title="Pesanan aktif"
        description="Antrean pesanan yang sudah dibayar. Perbarui status sesuai posisi meja."
        actions={
          <div className="kds-actions">
            <RealtimeBadge connected={realtime.connected} />
            <button type="button" className="btn btn-secondary" onClick={() => void load()}>
              <RefreshCw size={16} /> Muat ulang
            </button>
          </div>
        }
      />

      <div className="orders-toolbar">
        <div className="filter-row">
          {[
            ["all", "Semua"],
            ["queued", "Baru"],
            ["preparing", "Dimasak"],
            ["ready", "Siap"],
            ["served", "Disajikan"],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={`filter-chip${filter === value ? " is-active" : ""}`}
              onClick={() => setFilter(value)}
            >
              {label}
            </button>
          ))}
        </div>
        <label className="input-group orders-search">
          <Search size={16} />
          <input
            className="input"
            placeholder="Cari nomor, meja, atau WA"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
      </div>

      {error ? <div className="alert alert--danger">{error}</div> : null}

      {loading ? (
        <div className="empty-state empty-state--roomy">
          <ChefHat size={32} />
          <h3>Memuat pesanan...</h3>
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state empty-state--roomy">
          <UtensilsCrossed size={36} />
          <h3>Tidak ada pesanan</h3>
          <p>Pesanan baru akan muncul otomatis setelah pembayaran diterima.</p>
        </div>
      ) : (
        <div className="order-list">
          {filtered.map((order) => (
            <article className="order-card" key={order.id}>
              <header className="order-card__head">
                <div>
                  <strong>{order.orderNo ? `#${order.orderNo}` : "Pesanan"}</strong>
                  <span>{order.table?.label ?? "Tanpa meja"}</span>
                </div>
                <span className={`item-status item-status--${order.status}`}>
                  {statusLabels[order.status] ?? order.status}
                </span>
              </header>

              <div className="order-card__items">
                {order.items
                  .filter((item) => item.status !== "void")
                  .map((item) => (
                    <div className="order-card__item" key={item.id}>
                      <span>{item.qty}×</span>
                      <strong>{item.name}</strong>
                      <span className={`item-status item-status--${item.status}`}>
                        {statusLabels[item.status] ?? item.status}
                      </span>
                    </div>
                  ))}
              </div>

              <div className="order-card__meta">
                <span>{order.customerPhone ?? "Tanpa WA"}</span>
                {order.totals ? <strong>{formatRupiah(order.totals.total)}</strong> : null}
              </div>

              <footer className="order-card__actions">
                {order.status === "queued" && canKitchen ? (
                  <button
                    type="button"
                    className="btn btn-primary btn--sm"
                    disabled={busy === order.id}
                    onClick={() => void updateOrder(order, "preparing")}
                  >
                    <ChefHat size={15} /> Mulai masak
                  </button>
                ) : null}
                {order.status === "preparing" && canKitchen ? (
                  <button
                    type="button"
                    className="btn btn-primary btn--sm"
                    disabled={busy === order.id}
                    onClick={() => void updateOrder(order, "ready")}
                  >
                    <CheckCircle2 size={15} /> Siap
                  </button>
                ) : null}
                {order.status === "ready" && canServe ? (
                  <button
                    type="button"
                    className="btn btn-primary btn--sm"
                    disabled={busy === order.id}
                    onClick={() => void updateOrder(order, "served")}
                  >
                    <SkipForward size={15} /> Sudah disajikan
                  </button>
                ) : null}
                {order.status === "served" && canServe ? (
                  <button
                    type="button"
                    className="btn btn-secondary btn--sm"
                    disabled={busy === order.id}
                    onClick={() => void updateOrder(order, "completed")}
                  >
                    Selesaikan
                  </button>
                ) : null}
                {canCancel && !["served", "completed", "cancelled"].includes(order.status) ? (
                  <button
                    type="button"
                    className="btn btn-ghost btn--sm order-cancel"
                    disabled={busy === order.id}
                    onClick={() => {
                      if (window.confirm("Batalkan pesanan ini? Tindakan masuk audit.")) {
                        void updateOrder(order, "cancelled");
                      }
                    }}
                  >
                    <XCircle size={15} /> Batalkan
                  </button>
                ) : null}
              </footer>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
